import React, { useState, useCallback } from 'react';
import { MultiGrid, AutoSizer } from 'react-virtualized';
import Draggable from 'react-draggable';
import { Checkbox } from '@material-ui/core';
import moment from 'moment';
import LightTooltip from '../components/LightTooltip';

const DraggableHeader = props => {
  const { resizeColumns, columnSource, width } = props;

  const [deltaPositionX, setDeltaPositionX] = useState(0);

  const handleDrag = useCallback(
    async (e, ui) => {
      setDeltaPositionX(deltaPositionX + ui.deltaX);
    },
    [deltaPositionX, setDeltaPositionX]
  );

  const handleStop = useCallback(async () => {
    resizeColumns(columnSource, deltaPositionX, width);
    setDeltaPositionX(0);
  }, [resizeColumns, deltaPositionX, width, columnSource]);

  const dragStyle = {
    cursor: 'col-resize',
    color: 'transparent',
    position: 'absolute',
    right: -deltaPositionX,
    top: 0,
    zIndex: '1',
    width: '6px',
    borderRight: '1px solid #E0E0E0'
  };

  return (
    <Draggable
      axis="x"
      onDrag={handleDrag}
      onStop={handleStop}
      position={{ x: 0 }}
      zIndex={999}
    >
      <span style={dragStyle}>:</span>
    </Draggable>
  );
};

class VirtualizedGrid extends React.PureComponent {
  constructor() {
    super();
    this.state = {
      widths: {}, //fixed size width or percentage of flexible width grid part for each column
      definedSizeColumnsWidth: 0, //calculated width of all columns with defined constant widths
      orderBy: '',
      orderASC: false
    };
  }

  componentDidMount() {
    this.calculateWidths(false);
  }

  componentDidUpdate(prevProps) {
    if (this.props.columns.length !== prevProps.columns.length) {
      this.calculateWidths(true);
    }
  }

  calculateWidths = (recomputeGridSize = false) => {
    let widths = [];
    // number of columns with predefined constat width
    const fixedSizedColumns = this.props.columns.filter(e => e.width);
    // total width of columns with predefined constat width
    const definedSizeColumnsWidth = fixedSizedColumns
      .map(el => el.width)
      .reduce((acc, curr) => {
        acc += curr;
        return acc;
      }, 0);
    // number of columns with flexible width
    const relativeSizeColumnsLength =
      this.props.columns.length - fixedSizedColumns.length;

    this.props.columns.forEach(col => {
      widths[col.source] = col.width
        ? col.width
        : col.widthPercentage
        ? col.widthPercentage
        : 1 / relativeSizeColumnsLength;
    });
    this.setState(
      {
        widths: widths,
        definedSizeColumnsWidth: definedSizeColumnsWidth
      },
      recomputeGridSize ? this.gridRef.recomputeGridSize() : null
    );
  };

  columnWidthGetter = (column, totalWidth) => {
    let { widths, definedSizeColumnsWidth } = this.state;
    return column.width
      ? column.width
      : widths[column.source] * (totalWidth - definedSizeColumnsWidth);
  };

  resizeCol = (columnSource, deltaX, width) => {
    const { widths, definedSizeColumnsWidth } = this.state;
    const percentDelta = deltaX / (width - definedSizeColumnsWidth);
    const newColumnWidth = widths[columnSource] + percentDelta;
    if (newColumnWidth < 0) return;
    this.setState(
      {
        widths: {
          ...widths,
          [columnSource]: newColumnWidth
        }
      },
      () => this.gridRef.recomputeGridSize()
    );
  };

  sortFunction = (
    a,
    b,
    orderBy,
    orderASC,
    columnType,
    columnLookupOptions,
    columnLookupIdField,
    columnLookupTextField
  ) => {
    if (columnType === 'date') {
      if (!a[orderBy] || !b[orderBy]) {
        return a[orderBy] ? -1 : b[orderBy] ? 1 : 0;
      }
      if (moment(a[orderBy]).isSame(b[orderBy], 'day')) {
        return 0;
      }
      return orderASC
        ? moment(a[orderBy]).isBefore(b[orderBy], 'day')
          ? -1
          : 1
        : moment(a[orderBy]).isAfter(b[orderBy], 'day')
        ? -1
        : 1;
    } else if (columnType === 'lookup') {
      const aRowLookupObject = columnLookupOptions.find(
        e => e[columnLookupIdField] === a[orderBy]
      );
      const bRowLookupObject = columnLookupOptions.find(
        e => e[columnLookupIdField] === b[orderBy]
      );
      const aRowText = aRowLookupObject
        ? aRowLookupObject[columnLookupTextField] || ''
        : '';
      const bRowText = bRowLookupObject
        ? bRowLookupObject[columnLookupTextField] || ''
        : '';
      if (!aRowText || !bRowText) {
        return aRowText ? -1 : bRowText ? 1 : 0;
      }
      return orderASC
        ? aRowText.localeCompare(bRowText)
        : bRowText.localeCompare(aRowText);
    } else if (columnType === 'number') {
      if (!a[orderBy] || !b[orderBy]) {
        return a[orderBy] ? -1 : b[orderBy] ? 1 : 0;
      }
      if (a[orderBy] === b[orderBy]) {
        return 0;
      }
      return orderASC
        ? a[orderBy] < b[orderBy]
          ? -1
          : 1
        : a[orderBy] > b[orderBy]
        ? -1
        : 1;
    } else {
      if (!a[orderBy] || !b[orderBy]) {
        return a[orderBy] ? -1 : b[orderBy] ? 1 : 0;
      }
      return orderASC
        ? a[orderBy].trim().localeCompare(b[orderBy].trim(), undefined, {
            sensitivity: 'base'
          })
        : b[orderBy].trim().localeCompare(a[orderBy].trim(), undefined, {
            sensitivity: 'base'
          });
    }
  };

  //sort grid data with chosen column
  getSortedData = (orderBy, orderASC) => {
    const { data, columns } = this.props;
    let orderedData = [...data];
    const columnRow = orderedData[0]; //columns title row
    const orderColumn = columns.find(e => e.source === orderBy);
    const columnType = orderColumn.type;
    const columnLookupOptions = orderColumn.options
      ? orderColumn.options
      : undefined;
    const columnLookupTextField = orderColumn.lookupTextField
      ? orderColumn.lookupTextField
      : undefined;
    const columnLookupIdField = orderColumn.lookupIdField
      ? orderColumn.lookupIdField
      : undefined;
    //remove column header row and sort all remaining
    orderedData = orderedData
      .slice(1)
      .sort((a, b) =>
        this.sortFunction(
          a,
          b,
          orderBy,
          orderASC,
          columnType,
          columnLookupOptions,
          columnLookupIdField,
          columnLookupTextField
        )
      );
    //include again column header row
    orderedData.unshift(columnRow);
    return orderedData;
  };

  //set order column values in parent component data
  orderClick = column => {
    const { setDocuments } = this.props;
    const { orderBy, orderASC } = this.state;

    const newOrderBy = column.source;
    const newOrderASC = orderBy !== newOrderBy ? true : !orderASC;
    const sortedDocs = this.getSortedData(newOrderBy, newOrderASC);
    //function passed from parent component used to set order column values
    if (setDocuments) {
      setDocuments(sortedDocs);
    }
    this.setState({
      orderBy: newOrderBy,
      orderASC: newOrderASC
    });
  };

  cellRenderer = (row_data, width, data, orderBy, orderASC) => {
    let column = this.props.columns[row_data.columnIndex];
    let rowVal = data[row_data.rowIndex];
    const { resizibleColumns = true } = this.props;
    let cellVal = rowVal[column.source];

    let rowStyle = { ...row_data.style };
    rowStyle.width = this.columnWidthGetter(column, width);

    //fist row in data is column row
    if (row_data.rowIndex === 0) {
      const headerStyle = {
        ...rowStyle,
        color: '#757575',
        height: '30px',
        fontSize: '14px',
        fontWeight: '500',
        lineHeight: '30px',
        backgroundColor: '#F5F5F5 !important',
        textAlign: 'left'
      };

      if (column.type !== 'checkbox') {
        return (
          <LightTooltip
            key={row_data.key}
            placement="top"
            title={column.sortable ? 'Sort' : ''}
          >
            <div style={headerStyle}>
              <div
                style={{
                  borderRight: '1px solid #E0E0E0',
                  paddingLeft: column.additionalColumn ? '3px' : '8px',
                  width: 'calc(100% - 16px)',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  cursor: column.sortable ? 'pointer' : 'default'
                }}
                onClick={() =>
                  column.sortable ? this.orderClick(column) : null
                }
              >
                {cellVal}
                {column.sortable && (
                  <div
                    style={{
                      cursor: 'pointer',
                      position: 'absolute',
                      right: '35px',
                      top: '0px',
                      zIndex: 1,
                      width: '2px',
                      fontSize: 'smaller'
                    }}
                  >
                    {orderBy && orderBy === column.source && orderASC && (
                      <span
                        className="solstice-icon- solstice-icon-arrow-upward"
                        style={{ color: '#757575', transform: 'scale(0.9)' }}
                      />
                    )}
                    {orderBy && orderBy === column.source && !orderASC && (
                      <span
                        className="solstice-icon- solstice-icon-arrow-downward"
                        style={{ color: '#757575', transform: 'scale(0.9)' }}
                      />
                    )}
                    {(!orderBy || orderBy !== column.source) && (
                      <span style={{ color: 'transparent' }}>_</span>
                    )}
                  </div>
                )}
              </div>
              {resizibleColumns && (
                //add div for resizing with handler cursor type
                <DraggableHeader
                  resizeColumns={this.resizeCol}
                  columnSource={column.source}
                  width={width}
                />
              )}
            </div>
          </LightTooltip>
        );
      } else {
        const headercheckboxStyle = {
          ...rowStyle,
          padding: '0 8px !important',
          borderRight: '1px solid #E0E0E0'
        };
        const {
          disableSelectAll = false,
          selectedRows,
          filteredRows,
          filteredSelectedRows
        } = this.props;
        const numSelectedRows = selectedRows
          ? selectedRows
          : data.filter(r => r.selected === true).length;
        const rowCount = data.length - 1;
        const filteredRowsCount = filteredRows ? filteredRows.length : rowCount;
        const filteredSelectedRowsCount = filteredSelectedRows
          ? filteredSelectedRows.length
          : numSelectedRows;
        return (
          <div style={headercheckboxStyle} key={row_data.key}>
            {!disableSelectAll && (
              <Checkbox
                id="checkbox select all"
                indeterminate={
                  filteredSelectedRowsCount > 0 &&
                  filteredSelectedRowsCount < filteredRowsCount
                }
                checked={
                  filteredSelectedRowsCount > 0 &&
                  filteredSelectedRowsCount === filteredRowsCount
                }
                onClick={column.onClick}
              />
            )}
          </div>
        );
      }
      // data rows
    } else {
      if (column.renderer) {
        return column.renderer({ column, row_data, rowVal });
      } else {
        return (
          <div title={cellVal} style={row_data.style} key={row_data.key}>
            {cellVal}
          </div>
        );
      }
    }
  };

  render() {
    let {
      data,
      columns,
      fixedColumns,
      height,
      width,
      rowHeight,
      minHeight,
      itemSearch,
      emptyMessage,
      calculateColumnWidth = null
    } = this.props;
    let { widths, orderBy, orderASC } = this.state;
    let getColumnWidth = (colIndex, width) => {
      if (calculateColumnWidth) {
        return calculateColumnWidth(colIndex, width);
      }
      let column = columns[colIndex];
      const colWidth =
        column.type !== 'checkbox'
          ? widths[column.source] * width
          : column.width;
      if (!colWidth || isNaN(colWidth)) {
        return 100;
      } else {
        return colWidth;
      }
    };

    let getRowHeight = params => {
      return params.index === 0 ? 30 : rowHeight;
    };

    let noContentRenderer = () => {
      const emptyDiv = {
        height: '100%',
        width: '100%',
        fontSize: '0.875rem',
        backgroundColor: 'white',
        color: 'rgba(0, 0, 0, 0.87)',
        borderTop: '1px solid rgba(0, 0, 0, 0.12)'
      };
      const emptySpan = {
        display: 'flex',
        height: '100%',
        alignItems: 'center',
        justifyContent: 'center',
        margin: '-1px 0 0 0',
        borderBottom: '1px solid rgba(0, 0, 0, 0.12)'
      };
      if (emptyMessage !== '' && itemSearch === '') {
        return (
          <div style={emptyDiv}>
            <span style={emptySpan}>{emptyMessage}</span>
          </div>
        );
      }
      return null;
    };

    return (
      <div
        style={{
          height: height,
          width: width,
          minHeight: minHeight ? minHeight : height
        }}
      >
        <AutoSizer>
          {({ height, width }) => (
            <MultiGrid
              noContentRenderer={noContentRenderer}
              cellRenderer={row_data =>
                this.cellRenderer(row_data, width, data, orderBy, orderASC)
              }
              ref={grid => (this.gridRef = grid)}
              fixedColumnCount={fixedColumns}
              fixedRowCount={1}
              columnWidth={col => getColumnWidth(col.index, width)}
              columnCount={columns.length}
              enableFixedColumnScroll
              enableFixedRowScroll
              height={height}
              rowHeight={getRowHeight}
              rowCount={data.length}
              width={width}
              hideTopRightGridScrollbar
              hideBottomLeftGridScrollbar
              style={{
                border: '1px solid #E0E0E0',
                color: '#757575',
                backgroundColor: '#F5F5F5 !important'
              }}
              styleTopLeftGrid={{
                overflow: 'visible'
              }}
              styleBottomLeftGrid={{
                outline: 'none'
              }}
              styleBottomRightGrid={{
                outline: 'none'
              }}
            />
          )}
        </AutoSizer>
      </div>
    );
  }
}

export default VirtualizedGrid;
